import {
    useContext,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import ActionRequiredSection from "./views/actionRequiredSection";
import RecentSection from "./views/recentSection";
import StatsGrid from "./views/statsGrid";
import WelcomeSection from "./views/welcomeSection";
import {
    NCoreUIKitTheme,
    PageContainer,
    Loading
} from "ncore-ui-kit";
import {
    useNavigation
} from "@react-navigation/native";
import type RootStackParamList from "../../navigation/type";
import type {
    DashboardOverviewResponsePayloadType
} from "../../services/restAPI/actions/getDashboardOverview/type";
import type RequestTabType from "../requests/type";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    AuthContext
} from "../../contexts/AuthContext";
import RESTServiceContext from "../../services/restAPI/context";

const Dashboard = () => {
    const {
        width
    } = useWindowDimensions();

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const {
        activeOrganizationId,
        user
    } = useContext(AuthContext);

    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        overviewData,
        setOverviewData
    ] = useState<DashboardOverviewResponsePayloadType | null>(null);

    const [
        activeOrgName,
        setActiveOrgName
    ] = useState<string>("");

    const [
        activeOrgLogo,
        setActiveOrgLogo
    ] = useState<string>("");

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(true);

    const fetchDashboardOverview = async () => {
        setIsLoading(true);

        try {
            const res = await RESTServiceContext.action("getDashboardOverview", null);

            if (res?.payload) {
                setOverviewData(res.payload);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardOverview();
    }, []);

    useEffect(() => {
        if (!activeOrganizationId) {
            setActiveOrgName("");
            setActiveOrgLogo("");
            return;
        }

        if (user?.role !== "root") {
            const org = user?.organizations?.find(o => o.id === activeOrganizationId);
            setActiveOrgName(org?.name || "");
            return;
        }

        const org = user?.organizations?.find(o => o.id === activeOrganizationId);
        setActiveOrgName(org?.name || "");
        setActiveOrgLogo(org?.profilePicturePath || "");
    }, [
        activeOrganizationId,
        user
    ]);

    const handleNavigateToUsers = () => {
        navigation.navigate("MembersList");
    };

    const handleNavigateToEvents = () => {
        navigation.navigate("EventsList");
    };

    const handleNavigateToCompanies = () => {
        navigation.navigate("CompaniesList");
    };

    const handleNavigateToAnnouncements = () => {
        navigation.navigate("AnnouncementsList");
    };

    const handleNavigateToNews = () => {
        navigation.navigate("NewsList");
    };

    const handleNavigateToDrive = () => {
        navigation.navigate("Drive");
    };

    const handleNavigateToMemberDetail = (userID: string) => {
        navigation.navigate("MemberDetail", {
            userID
        });
    };

    const handleNavigateToEventDetail = (eventID: string) => {
        navigation.navigate("EventEdit", {
            eventID
        });
    };

    const handleNavigateToRequests = (tab?: RequestTabType) => {
        navigation.navigate("Requests", tab ? {
            initialTab: tab
        } : undefined);
    };

    const displayName = user?.fullName || user?.name_surname || (user?.firstName ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ""}` : "") || user?.username || "";

    const renderContent = () => {
        if (isLoading) {
            return <View
                style={stylesheet.loadingContainer}
            >
                <Loading
                    size={36}
                />
            </View>;
        }

        return <View
            style={[
                stylesheet.contentWrapper,
                {
                    gap: spaces.spacingXl
                }
            ]}
        >
            <WelcomeSection
                activeOrganizationId={activeOrganizationId}
                isGuest={user?.role === "guest"}
                activeOrgLogo={activeOrgLogo}
                activeOrgName={activeOrgName}
                displayName={displayName}
            />
            {
                overviewData?.actionRequired ?
                    <ActionRequiredSection
                        pendingParticipants={overviewData.actionRequired.pendingEventParticipants}
                        unverifiedUsers={overviewData.actionRequired.unverifiedUsers}
                        onNavigateToRequests={handleNavigateToRequests}
                        requests={overviewData.actionRequired.requests}
                        onNavigateToEvents={handleNavigateToEvents}
                        onNavigateToUsers={handleNavigateToUsers}
                    />
                :
                    null
            }
            {
                overviewData?.stats ?
                    <StatsGrid
                        onNavigateToAnnouncements={handleNavigateToAnnouncements}
                        onNavigateToCompanies={handleNavigateToCompanies}
                        onNavigateToEvents={handleNavigateToEvents}
                        onNavigateToDrive={handleNavigateToDrive}
                        onNavigateToUsers={handleNavigateToUsers}
                        onNavigateToNews={handleNavigateToNews}
                        stats={overviewData.stats}
                    />
                :
                    null
            }
            {
                overviewData?.recent ?
                    <RecentSection
                        onNavigateToMemberDetail={handleNavigateToMemberDetail}
                        onNavigateToEventDetail={handleNavigateToEventDetail}
                        onNavigateToMembersList={handleNavigateToUsers}
                        onNavigateToEventsList={handleNavigateToEvents}
                        recent={overviewData.recent}
                    />
                :
                    null
            }
        </View>;
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: width < 768 ? spaces.spacingMd : spaces.spacingXl
                }
            ]}
        >
            {renderContent()}
        </View>
    </PageContainer>;
};
export default Dashboard;
